import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CampaignDatesService } from '@campaign-form/form-fields/campaign-basics/campaign-dates/campaign-dates.service';
import { ExecutionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.component';
import {
  IExecution,
  IExecutionForm,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { ExecutionService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { parseDate } from '../../brief-wizard-dialog.util';
import { IBriefStepState } from '../brief-step.interface';

// A CampaignDatesService instance of its own (it's normally providedIn: 'root', shared campaign-
// wide) - the wizard isn't inside a real campaign-form, so the real service's global start/end
// dates would either be stale or clash with one if a campaign-form happens to be open elsewhere.
@Component({
  imports: [ExecutionComponent, DatePipe, TranslateModule],
  providers: [CampaignDatesService],
  selector: 'app-brief-execution-item-step',
  styleUrl: './execution-item-step.component.scss',
  templateUrl: './execution-item-step.component.html',
})
export class BriefExecutionItemStepComponent {
  public readonly initialValue = input.required<Record<string, unknown>>();
  public readonly platformFeature = input<EPlatformFeature | null>(null);
  public readonly campaignStart = input<Date | null>(null);
  public readonly campaignEnd = input<Date | null>(null);
  // Shown as a hint only. ExecutionComponent gets no game plays: its per-game-play duration check
  // doesn't match the backend's (their sum), and the wizard lengthens a short execution itself
  // (fitExecutionDurationsToGamePlays) - blocking Next on it would leave no way out when the game
  // plays don't fit the campaign.
  public readonly gamePlaysDuration = input<number>(0);
  public readonly stateChange = output<IBriefStepState<Record<string, unknown>>>();

  // A computed (not a plain field reassigned inside an effect) so every consumer - this
  // template's [form] binding, and nested leaf components' own [control]="form().get(...)"
  // bindings on mat-error - is guaranteed to read the SAME, current FormGroup instance. See the
  // matching comment on BriefDeliverableItemStepComponent's own `form` for the bug this avoids.
  public readonly form = computed<FormGroup<IExecutionForm> | null>(() => {
    const platformFeature = this.platformFeature();
    const initialValue = this.initialValue();

    if (!platformFeature) {
      return null;
    }

    return this.executionService.getConfig().createForm(platformFeature, {
      ...initialValue,
      executionDate: parseDate(initialValue['executionDate']),
      executionEndDate: parseDate(initialValue['executionEndDate']),
      executionStartDate: parseDate(initialValue['executionStartDate']),
      executionTime: parseDate(initialValue['executionTime']),
    } as unknown as IExecution);
  });

  private readonly campaignDatesService = inject(CampaignDatesService);
  private readonly executionService = inject(ExecutionService);

  constructor() {
    effect(() => {
      const start = this.campaignStart();
      const end = this.campaignEnd();

      if (start) {
        this.campaignDatesService.setStartDate(start);
      }

      if (end) {
        this.campaignDatesService.setEndDate(end);
      }
    });

    effect((onCleanup) => {
      const form = this.form();

      if (!form) {
        return;
      }

      form.markAllAsTouched();
      this.emitState(form);

      // ExecutionComponent extends CustomCtaBaseDirective, whose own ngOnInit()
      // unconditionally calls form.markAsUntouched()/markAsPristine() right after this effect
      // runs, silently undoing the markAllAsTouched() above so mat-error stays hidden. See the
      // matching comment in BriefDeliverableItemStepComponent for the full explanation.
      const timeoutId = setTimeout(() => form.markAllAsTouched());

      const subscription = form.valueChanges.subscribe(() => this.emitState(form));

      onCleanup(() => {
        clearTimeout(timeoutId);
        subscription.unsubscribe();
      });
    });
  }

  private emitState(form: FormGroup<IExecutionForm>): void {
    const rawValue = form.getRawValue() as unknown as Record<string, unknown>;

    // The `duration` input is `type="text"` + `numberOnly` (shared with the real campaign-form's
    // ExecutionComponent), so its control value is a plain string, not the `number | null`
    // IExecutionForm['duration'] claims - deleting a previously-typed value leaves '', which the
    // backend rejects (it expects an integer or null).
    if (rawValue['duration'] === '') {
      rawValue['duration'] = null;
    }

    this.stateChange.emit({ valid: form.valid, value: rawValue });
  }
}
